/**
 * The person's own side of sign-in MCP connections: which systems their groups
 * give them, whether they are signed in to each, and connecting or
 * disconnecting. Connect leaves the page for the login system; the API sends
 * the person back to this page when they are done.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../../hooks/useMe.js';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { McpMyConnection, McpSignInStartResponse } from '../../lib/types.js';

export const CONNECTED_ACCOUNTS_PATH = '/settings/connected-accounts';

export interface ConnectedAccountsLogic {
  connections: McpMyConnection[];
  isLoading: boolean;
  error: Error | null;
  connect: (integrationId: string) => Promise<void>;
  disconnect: (integrationId: string) => Promise<void>;
  connectingId: string | null;
  disconnectingId: string | null;
}

/** Where the login system sends the person back: this page, on this origin. */
const returnUrl = (): string => `${window.location.origin}${CONNECTED_ACCOUNTS_PATH}`;

export const useConnectedAccountsLogic = (
  navigateTo: (url: string) => void = (url) => window.location.assign(url),
): ConnectedAccountsLogic => {
  const me = useMe();
  const queryClient = useQueryClient();
  const tenantId = me.data?.tenant.id ?? '';
  const userId = me.data?.user.id ?? '';
  const queryKey = queryKeys.mcpMyConnections(tenantId, userId);
  const query = useQuery({
    queryKey,
    enabled: Boolean(me.data),
    queryFn: () =>
      apiRequest<{ connections: McpMyConnection[] }>('/api/integrations/mcp-connections/mine'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey });

  const connect = useMutation<void, Error, string>({
    mutationFn: async (integrationId) => {
      const started = await apiRequest<McpSignInStartResponse>(
        `/api/integrations/${integrationId}/mcp-sign-in`,
        { method: 'POST', body: { returnUrl: returnUrl() } },
      );
      if (started.status === 'redirect' && started.authorizeUrl) navigateTo(started.authorizeUrl);
    },
    onSuccess: refresh,
  });
  const disconnect = useMutation<void, Error, string>({
    mutationFn: (integrationId) =>
      apiRequest<void>(`/api/integrations/${integrationId}/mcp-sign-in`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  return {
    connections: query.data?.connections ?? [],
    isLoading: me.isLoading || query.isLoading,
    error: query.error ?? connect.error ?? disconnect.error,
    connect: (integrationId) => connect.mutateAsync(integrationId),
    disconnect: (integrationId) => disconnect.mutateAsync(integrationId),
    connectingId: connect.isPending ? connect.variables : null,
    disconnectingId: disconnect.isPending ? disconnect.variables : null,
  };
};
